<script lang="ts">
  /** The overall palette (for every layer without its own), its hue shift, and the palette editor. */
  import type { AppController } from '../../app/controller.svelte';
  import { paletteFromRef } from '../../engine/render/palettes';
  import PalettePicker from './PalettePicker.svelte';
  import ParamControl from './ParamControl.svelte';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  const scene = $derived(ctl.scene);
  const custom = $derived(ctl.libraryVersion >= 0 ? ctl.library.listPalettes() : []);
  let editing = $state<{ id?: string; name: string; stops: string[]; cyclic: boolean } | null>(
    null,
  );

  function editCurrent() {
    const p = paletteFromRef(scene.palette);
    editing = {
      ...(custom.some((c) => c.id === scene.palette.id) ? { id: scene.palette.id } : {}),
      name: custom.some((c) => c.id === scene.palette.id) ? p.name : `${p.name} (custom)`,
      stops: [...p.stops],
      cyclic: !!p.cyclic,
    };
  }

  function applyEditing() {
    if (!editing) return;
    ctl.dispatch(
      {
        type: 'palette.set',
        palette: {
          id: editing.id ?? 'custom',
          name: editing.name,
          stops: [...editing.stops],
          ...(editing.cyclic ? { cyclic: true } : {}),
        },
      },
      'palette-edit',
    );
  }

  async function saveEditing() {
    if (!editing) return;
    const saved = await ctl.library.savePalette({
      ...(editing.id ? { id: editing.id } : {}),
      name: editing.name,
      stops: editing.stops,
      cyclic: editing.cyclic,
    });
    ctl.dispatch({
      type: 'palette.set',
      palette: {
        id: saved.id,
        name: saved.name,
        stops: saved.stops,
        ...(saved.cyclic ? { cyclic: true } : {}),
      },
    });
    editing = null;
    ctl.toast(`Saved palette “${saved.name}”.`);
  }
</script>

<div data-tour="palette">
  <div class="row end">
    <button class="link" onclick={editCurrent}>Edit palette…</button>
  </div>
  <PalettePicker
    {ctl}
    current={scene.palette}
    onpick={(palette) => ctl.dispatch({ type: 'palette.set', palette })}
  />
  <ParamControl
    {ctl}
    def={{ id: 'shift', label: 'Hue shift', type: 'float', min: 0, max: 1, default: 0 }}
    value={scene.paletteShift}
    target="palette/shift"
    midiPath="palette/shift"
    onchange={(v) => ctl.dispatch({ type: 'palette.shift', value: v }, 'palette-shift')}
  />

  {#if editing}
    <div class="editor" data-testid="palette-editor">
      <div
        class="preview"
        style:background={`linear-gradient(90deg, ${editing.stops.join(', ')})`}
      ></div>
      <div class="stops">
        {#each editing.stops as s, i (i)}
          <div class="stop">
            <input
              type="color"
              value={s.length === 4 ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}` : s}
              aria-label={`Colour ${i + 1}`}
              oninput={(e) => {
                editing!.stops[i] = e.currentTarget.value;
                applyEditing();
              }}
            />
            {#if editing.stops.length > 2}
              <button
                class="icon-btn"
                aria-label="Remove colour"
                onclick={() => {
                  editing!.stops.splice(i, 1);
                  applyEditing();
                }}><Icon name="close" /></button
              >
            {/if}
          </div>
        {/each}
        {#if editing.stops.length < 12}
          <button
            class="icon-btn"
            aria-label="Add colour"
            onclick={() => {
              editing!.stops.push(editing!.stops.at(-1) ?? '#ffffff');
              applyEditing();
            }}><Icon name="plus" /></button
          >
        {/if}
      </div>
      <label class="field"><span>Name</span><input type="text" bind:value={editing.name} /></label>
      <label class="row small muted"
        ><input type="checkbox" bind:checked={editing.cyclic} onchange={applyEditing} /> Wraps around
        (cyclic)</label
      >
      <div class="row">
        <button class="primary" onclick={saveEditing}>Save palette</button>
        <button onclick={() => (editing = null)}>Close</button>
        {#if editing.id}
          <button
            class="link"
            onclick={async () => {
              await ctl.library.removePalette(editing!.id!);
              editing = null;
            }}>Delete</button
          >
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .end {
    justify-content: flex-end;
  }
  .editor {
    margin-top: 10px;
    display: grid;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  .preview {
    height: 22px;
    border-radius: 6px;
  }
  .stops {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .stop {
    display: flex;
    align-items: center;
  }
  input[type='color'] {
    width: 34px;
    height: 26px;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: none;
  }
</style>
